import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faLink } from "@fortawesome/pro-regular-svg-icons";
import API from "Signups/API.js";
import { SignupsAppState } from "Signups/state.js";

import FnrButton from "UI/FnrButton3";
import FnrHoverButton from "UI/FnrHoverButton";
import FnrIcon from "UI/FnrIcon";
import {
  FnrPopover,
  FnrPopoverTriggerButton,
  FnrPopoverWrapper,
} from "UI/FnrPopover2";
import { FnrToggleChild } from "UI/FnrToggle2";

import { useGetCurrentSignupList } from "Utilities/Hooks/useGetCurrentSignupList";

import { Icon, RespondentsDataButton, SubText, Text, Title } from "./styles";

export default function CreateLinkedGroups({ disabled }) {
  const [, dispatch] = useContext(SignupsAppState);
  const signupList = useGetCurrentSignupList();
  const [closeTrigger, setCloseTrigger] = useState(0);
  const [status, setStatus] = useState(null);
  const { linkedToGroup } = signupList;

  const [showPopover, setShowPopover] = useState(false);
  const buttonRef = useRef();

  const toggleShowPopover = () => {
    setShowPopover((currentValue) => !currentValue);
  };

  const signupHasLinkedItems = signupList?.signupItems?.some(
    (item) => item.linkedToGroup?.ID
  );

  const closeAlert = () => {
    toggleShowPopover();
    setStatus(null);
  };

  const handleGoToGroup = () => {
    window.fnReactBus.goToUrl(linkedToGroup?.shortname);
  };

  const handleLinkClick = () => {
    setStatus("loading");

    API.createLinkedGroups({
      signupID: signupList.ID,
    })
      .post()
      .then((responseData) => {
        setStatus("success");
        dispatch({
          type: "Set Current Signup List",
          signupList: responseData.signupList,
        });
        window.fnReactBus.reloadCurrentPage();
        closeAlert();
      })
      .catch(() => {
        setStatus("error");
      });
  };

  const getPhotoSrc = () => {
    if (linkedToGroup?.photoURL) {
      return linkedToGroup.photoURL;
    } else {
      return "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg";
    }
  };

  const renderPopoverContent = () => {
    if (status === "error") {
      return (
        <PopoverContent>
          Uh oh! There was a problem unlinking from the group. Please try again
          later, or contact{" "}
          <a href="mailto:help@flocknote.com">help@flocknote.com</a> for help.
          <AlertButtons>
            <FnrButton onClick={closeAlert}>Ok</FnrButton>
          </AlertButtons>
        </PopoverContent>
      );
    } else if (signupHasLinkedItems) {
      return (
        <PopoverContent>
          In order to create linked groups, please unlink any Signup items that
          are currently linked.
          <AlertButtons>
            <FnrButton onClick={closeAlert}>Cancel</FnrButton>
          </AlertButtons>
        </PopoverContent>
      );
    } else {
      return (
        <PopoverContent>
          This will create and link a new group to this Signup, as well as for
          each Signup item. When people sign up, they will automatically be
          added to these groups.
          <AlertButtons>
            <FnrButton onClick={closeAlert}>Cancel</FnrButton>
            <FnrButton
              color="blue"
              onClick={handleLinkClick}
              loading={status === "loading"}
            >
              Yes please!
            </FnrButton>
          </AlertButtons>
        </PopoverContent>
      );
    }
  };

  if (linkedToGroup?.ID) {
    return (
      <FnrToggleChild>
        <RespondentsDataButton onClick={handleGoToGroup}>
          <Icon>
            <GroupImg backgroundImg={getPhotoSrc()} />
          </Icon>
          <Text>
            <Title>Linked to {linkedToGroup?.name}</Title>
            <SubText>
              {
                "Members will automatically be added to this group and its subgroups as they sign up."
              }
            </SubText>
          </Text>
        </RespondentsDataButton>
      </FnrToggleChild>
    );
  }

  return (
    <Wrapper>
      <FnrToggleChild forceHover={showPopover} disabled={disabled}>
        <RespondentsDataButton ref={buttonRef} onClick={toggleShowPopover}>
          <Icon>
            <FnrIcon fad fa-network-wired />
          </Icon>
          <Text>
            <Title>Create linked groups</Title>
            <SubText>
              {
                "Create a group for each item and automatically add members to them."
              }
            </SubText>
          </Text>
        </RespondentsDataButton>
      </FnrToggleChild>

      <FnrPopover
        show={showPopover}
        onShouldClose={toggleShowPopover}
        buttonRef={buttonRef}
      >
        {renderPopoverContent()}
      </FnrPopover>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const GroupImg = styled.div`
  height: 38px;
  width: 38px;
  background-color: #fff;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url(${(props) => props.backgroundImg});
  border: 2px solid #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 1px 1px 3px;
  border-radius: 100%;
`;

const PopoverContent = styled.div`
  padding: 10px;
  width: 240px;
`;

const AlertButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
`;
